import type React from 'react';
import { useState, useMemo } from 'react';
import {
  ThreadPrimitive,
  MessagePrimitive,
  ActionBarPrimitive,
  useMessage,
} from '@assistant-ui/react';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { IvyMarkdownContent } from './IvyMarkdownContent';
import { IvyToolFallback } from './IvyToolFallback';
import { OutOfCreditsCard } from '@/components/billing/OutOfCreditsCard';
import { parseOutOfCreditsPart } from '@/services/billingPaywallDetector';
import { parseErrorPart, parseTurnMeta } from '@/lib/ivyChat/chatNotices';
import { stepsDoneLabel } from '@/lib/ivyChat/stepWords';
import { ChatErrorNotice, TurnMeta } from './ChatNotices';
import { EmbeddedIvyThreadB } from './EmbeddedIvyThreadB';
import { StaleMessageGuard } from './StaleMessageGuard';
import { studioBOn } from '@/lib/studioB/flag';
import { formatRelativeTime } from '@/lib/utils';
import {
  Copy, Check, RefreshCw, Wand2,
  ChevronRight, ChevronDown, Zap, Minimize2, Sparkles,
} from 'lucide-react';

export function EmbeddedIvyThread(props: React.ComponentProps<typeof EmbeddedIvyThreadB>) {
  if (studioBOn()) return <EmbeddedIvyThreadB {...props} />;
  return (
    <ThreadPrimitive.Root className="flex flex-col flex-1 min-h-0">
      <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto custom-scrollbar">
        <ThreadPrimitive.Empty>
          <EmbeddedWelcome />
        </ThreadPrimitive.Empty>

        <div className="px-3 py-4">
          <StaleMessageGuard thread="A">
            <ThreadPrimitive.Messages
              components={{
                UserMessage: EmbeddedUserMessage,
                AssistantMessage: EmbeddedAssistantMessage,
              }}
            />
          </StaleMessageGuard>
          <EmbeddedStreamingIndicator />
        </div>

        <ThreadPrimitive.ViewportFooter className="sticky bottom-0">
          <EmbeddedScrollButton />
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
}

function EmbeddedWelcome() {
  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-center gap-2">
      <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
        <Wand2 className="w-4 h-4 text-brand" />
      </div>
      <p className="text-sm font-medium text-foreground">Ivy</p>
      <p className="text-xs text-muted-foreground max-w-[220px]">
        Ask me to edit slides, write narration, or refine the outline.
      </p>
    </div>
  );
}

function MessageTimestamp() {
  const { createdAt } = useMessage();
  if (!createdAt) return null;
  return (
    <span
      className="text-[10px] text-muted-foreground/40 select-none"
      title={createdAt.toLocaleString()}
    >
      {formatRelativeTime(createdAt)}
    </span>
  );
}

function EmbeddedUserMessage() {
  return (
    <MessagePrimitive.Root className="flex flex-col items-end gap-0.5 py-1.5 animate-fade-in">
      <div className="max-w-[90%]">
        <div className="px-3 py-2 rounded-2xl bg-primary text-primary-foreground text-[13px]">
          <MessagePrimitive.Content
            components={{
              Text: ({ text }) => <span className="whitespace-pre-wrap">{text}</span>,
            }}
          />
        </div>
      </div>
      <div className="pr-1">
        <MessageTimestamp />
      </div>
    </MessagePrimitive.Root>
  );
}

function EmbeddedAssistantMessage() {
  const messageState = useMessage();
  const content = (messageState as any).content || [];
  const isCompaction = content.length === 1
    && content[0].type === 'text'
    && content[0].text?.startsWith('[COMPACTION_SUMMARY]\n');

  if (isCompaction) {
    return (
      <MessagePrimitive.Root className="py-1 animate-fade-in w-full">
        <EmbeddedGroupedContent />
      </MessagePrimitive.Root>
    );
  }

  return (
    <MessagePrimitive.Root className="flex py-1.5 animate-fade-in group">
      <div className="flex items-start gap-2 w-full">
        <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Wand2 className="w-3 h-3 text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <EmbeddedGroupedContent />
          <div className="mt-0.5 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <MessageTimestamp />
            <EmbeddedActionBar />
          </div>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

type Segment =
  | { type: 'text'; text: string }
  | { type: 'steps'; parts: any[] };

function EmbeddedGroupedContent() {
  const messageState = useMessage();
  const isRunning = messageState.status?.type === 'running';
  const checkpoint = { active: !!messageState.isLast && !isRunning, stale: !messageState.isLast };
  const content = (messageState as any).content || [];

  const segments = useMemo(() => {
    const result: Segment[] = [];
    let currentSteps: any[] = [];

    const flushSteps = () => {
      if (currentSteps.length > 0) {
        result.push({ type: 'steps', parts: currentSteps });
        currentSteps = [];
      }
    };

    for (const part of content) {
      if (part.type === 'text') {
        const text = part.text || '';
        if (text.trim()) {
          flushSteps();
          result.push({ type: 'text', text });
        }
      } else if (part.type === 'reasoning' || part.type === 'tool-call') {
        currentSteps.push(part);
      }
    }
    flushSteps();
    return result;
  }, [content]);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return <EmbeddedAssistantText key={`t-${i}`} text={seg.text} checkpoint={checkpoint} />;
        }
        return (
          <EmbeddedStepsBlock
            key={`s-${i}`}
            parts={seg.parts}
            isRunning={isRunning && i === segments.length - 1}
          />
        );
      })}
    </>
  );
}

function EmbeddedStepsBlock({ parts, isRunning }: { parts: any[]; isRunning: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const toolCount = parts.filter(p => p.type === 'tool-call').length;
  const thinkCount = parts.filter(p => p.type === 'reasoning').length;

  let label: string;
  if (isRunning) {
    label = toolCount > 0
      ? `${toolCount} step${toolCount !== 1 ? 's' : ''} running...`
      : 'Thinking...';
  } else if (toolCount > 0) {
    label = stepsDoneLabel(toolCount, parts.filter(p => p.type === 'tool-call' && p.isError).length);
  } else {
    label = `Thought ${thinkCount} time${thinkCount !== 1 ? 's' : ''}`;
  }

  return (
    <div className="my-0.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-muted/30 transition-colors text-left"
      >
        <Zap className={`w-2.5 h-2.5 flex-shrink-0 ${isRunning ? 'text-info' : 'text-muted-foreground/40'}`} />
        <span className="text-[11px] text-muted-foreground/60">{label}</span>
        {isRunning && (
          <div className="flex gap-0.5 ml-0.5">
            <div className="w-1 h-1 rounded-full bg-info/60 typing-dot" />
            <div className="w-1 h-1 rounded-full bg-info/60 typing-dot" />
            <div className="w-1 h-1 rounded-full bg-info/60 typing-dot" />
          </div>
        )}
        <ChevronRight className={`w-2.5 h-2.5 text-muted-foreground/30 transition-transform ml-auto ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="ml-2 mt-0.5 pl-2 border-l border-border-secondary space-y-px">
          {parts.map((part, i) => {
            if (part.type === 'reasoning') {
              return (
                <div key={`r-${i}`} className="px-1.5 py-0.5">
                  <pre className="text-[10px] text-muted-foreground/40 italic whitespace-pre-wrap font-sans leading-relaxed max-h-20 overflow-y-auto custom-scrollbar">
                    {(part.text || '').slice(0, 200)}
                  </pre>
                </div>
              );
            }
            if (part.type === 'tool-call') {
              return (
                <div key={part.toolCallId || `tc-${i}`} className="py-px">
                  <IvyToolFallback
                    toolName={part.toolName || 'unknown'}
                    args={part.args || {}}
                    result={part.result}
                    argsText={part.argsText}
                    status={part.status || { type: 'complete' }}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

function EmbeddedAssistantText({ text, checkpoint }: { text: string; checkpoint: { active: boolean; stale: boolean } }) {
  if (!text.trim()) return null;
  if (text.startsWith('[COMPACTION_SUMMARY]\n')) {
    return <CompactionCard summaryText={text.slice('[COMPACTION_SUMMARY]\n'.length)} />;
  }
  const paywall = parseOutOfCreditsPart(text);
  if (paywall) return <OutOfCreditsCard paywall={paywall} />;
  const error = parseErrorPart(text);
  if (error !== null) return <ChatErrorNotice message={error} />;
  const meta = parseTurnMeta(text);
  if (meta !== null) return <TurnMeta text={meta} />;
  return (
    <div className="prose-chat text-[13px]">
      <IvyMarkdownContent content={text} checkpoint={checkpoint} />
    </div>
  );
}

function CompactionCard({ summaryText }: { summaryText: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left"
      >
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-accent/40" />
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/60 border border-primary/30 hover:border-primary/30 dark:hover:border-primary transition-colors">
            <Minimize2 className="w-2.5 h-2.5 text-primary" />
            <span className="text-[11px] font-medium text-primary dark:text-accent-foreground">
              Context compacted
            </span>
            {expanded
              ? <ChevronDown className="w-2.5 h-2.5 text-primary/60" />
              : <ChevronRight className="w-2.5 h-2.5 text-primary/60" />
            }
          </div>
          <div className="flex-1 h-px bg-accent/40" />
        </div>
      </button>
      {expanded && (
        <div className="mt-1.5 px-2.5 py-2 rounded-lg bg-accent/40 border border-primary/30 max-h-[200px] overflow-y-auto custom-scrollbar">
          <p className="text-[11px] text-primary/70 dark:text-accent-foreground/60 whitespace-pre-wrap leading-relaxed">
            {summaryText}
          </p>
        </div>
      )}
    </div>
  );
}

function EmbeddedStreamingIndicator() {
  const isStreaming = useEmbeddedIvyChatStore(s => s.isStreaming);
  const activeTurnKind = useEmbeddedIvyChatStore(s => s.activeTurnKind);
  const messages = useEmbeddedIvyChatStore(s => s.messages);
  if (!isStreaming) return null;
  if (activeTurnKind === 'learn') return <LearningIndicator />;

  const last = messages[messages.length - 1];
  // Hide when the last event is a tool step (StepsBlock handles its own indicator)
  // or a result event (turn is complete, stream close may be slightly delayed)
  if (last?.type === 'tool_use' || last?.type === 'tool_result' || last?.type === 'result') return null;

  const label = last?.type === 'thinking' ? 'Reasoning...' : 'Thinking...';

  return (
    <div className="flex items-center gap-1.5 py-1.5 pl-[34px] animate-fade-in">
      <div className="flex gap-0.5">
        <div className="w-1 h-1 rounded-full bg-muted-foreground/40 typing-dot" />
        <div className="w-1 h-1 rounded-full bg-muted-foreground/40 typing-dot" />
        <div className="w-1 h-1 rounded-full bg-muted-foreground/40 typing-dot" />
      </div>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

function LearningIndicator() {
  return (
    <div className="flex items-center gap-1.5 py-1.5 pl-[34px] animate-fade-in">
      <Sparkles className="w-3 h-3 text-brand/70 animate-pulse" />
      <span className="text-[11px] text-muted-foreground">Learning from this session…</span>
    </div>
  );
}

function EmbeddedActionBar() {
  const [copied, setCopied] = useState(false);
  return (
    <ActionBarPrimitive.Root className="flex items-center gap-0.5">
      <ActionBarPrimitive.Copy
        onClick={() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        asChild
      >
        <button className="p-0.5 rounded text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors">
          {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
        </button>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <button className="p-0.5 rounded text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors">
          <RefreshCw className="w-3 h-3" />
        </button>
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
}

function EmbeddedScrollButton() {
  return (
    <div className="flex justify-center pb-1">
      <ThreadPrimitive.ScrollToBottom asChild>
        <button className="px-2 py-1 rounded-full bg-surface-elevated border border-border text-[10px] text-muted-foreground hover:text-foreground shadow-sm transition-all">
          Scroll to bottom
        </button>
      </ThreadPrimitive.ScrollToBottom>
    </div>
  );
}
